import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Copy, MessageCircle, Upload } from "lucide-react";
import { api, formatRupiah } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { waLink, orderWaMessage } from "@/lib/whatsapp";
import GreetingCardPreview from "@/components/store/GreetingCardPreview";

export default function Checkout() {
  const { items, total, clear } = useCart();
  const [form, setForm] = useState({ buyer_name: "", phone: "", address: "", note: "" });
  const [banks, setBanks] = useState([]);
  const [wa, setWa] = useState({ number: "", display: "" });
  const [submitting, setSubmitting] = useState(false);
  const [order, setOrder] = useState(null);
  const [proofUploading, setProofUploading] = useState(false);
  const [proofDone, setProofDone] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api
      .get("/config")
      .then((r) => {
        setBanks(r.data.bank_accounts || []);
        setWa({ number: r.data.whatsapp || "", display: r.data.whatsapp_display || "" });
      })
      .catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error("Keranjang masih kosong");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        items: items.map((i) => ({
          product_id: i.product.id,
          name: i.product.name,
          price: i.product.price,
          qty: i.qty,
        })),
      };
      const { data } = await api.post("/orders", payload);
      setOrder(data);
      clear();
      toast.success(`Pesanan ${data.order_code} berhasil dibuat`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Gagal membuat pesanan, coba lagi");
    } finally {
      setSubmitting(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text.replace(/-/g, ""));
    toast.success("Nomor rekening disalin");
  };

  const uploadProof = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !order) return;
    setProofUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/orders/${order.id}/proof`, fd);
      setProofDone(true);
      toast.success("Bukti transfer berhasil diunggah");
    } catch {
      toast.error("Gagal mengunggah bukti transfer");
    } finally {
      setProofUploading(false);
    }
  };

  return (
    <section id="checkout" className="py-20 bg-[#ECE5D8] border-b border-[#DCD3C3]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <span className="text-xs uppercase tracking-widest text-[#3D5A40] font-semibold">
            Formulir Checkout Toko
          </span>
          <h2 className="font-serif-display text-3xl sm:text-4xl font-normal text-[#1E2B1D] mt-2">
            Selesaikan Pesanan Bunga
          </h2>
          <p className="text-xs sm:text-sm text-[#5E6D5B] mt-2">
            Isi informasi pengiriman di bawah ini. Nomor rekening resmi akan muncul untuk proses
            transfer manual.
          </p>
        </div>

        <div className="bg-[#FBF9F5] rounded-3xl p-6 sm:p-10 border border-[#DACFBE] shadow-md">
          {order ? (
            <div data-testid="checkout-success" className="text-center">
              <CheckCircle2 className="w-14 h-14 text-[#3D5A40] mx-auto mb-4" />
              <h3 className="font-serif-display text-2xl font-bold text-[#1E2B1D]">
                Pesanan Berhasil Dibuat
              </h3>
              <p className="text-sm text-[#5E6D5B] mt-2">
                Kode pesanan Anda:{" "}
                <span data-testid="order-code" className="font-bold text-[#3D5A40]">
                  {order.order_code}
                </span>
              </p>
              <p className="text-sm text-[#5E6D5B] mt-1">
                Total yang harus ditransfer:{" "}
                <span data-testid="order-total" className="font-bold text-[#203120]">
                  {formatRupiah(order.total)}
                </span>
              </p>

              <div className="p-5 rounded-2xl bg-[#F4EFE6] border border-[#E0D7C6] mt-8 text-left">
                <span className="text-xs font-bold uppercase tracking-wider text-[#3D5A40] block mb-2">
                  Transfer ke Rekening Resmi Puspita Florist
                </span>
                <p className="text-xs text-[#5E6D5B] mb-4">
                  Lakukan transfer sesuai total nominal ke salah satu rekening berikut:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  {banks.map((b, i) => (
                    <div
                      key={i}
                      data-testid={`bank-account-${i + 1}`}
                      className="p-3 bg-white rounded-xl border border-[#E8E2D5] flex items-center justify-between gap-2"
                    >
                      <div>
                        <span className="font-bold text-[#1E2B1D] block">{b.bank}</span>
                        <span className="font-mono text-xs text-[#3D5A40] font-bold block mt-1">
                          {b.number}
                        </span>
                        <span className="text-[10px] text-[#7E8F7A] block">a.n. {b.holder}</span>
                      </div>
                      <button
                        data-testid={`copy-account-${i + 1}`}
                        onClick={() => copy(b.number)}
                        className="p-2 rounded-lg border border-[#D5CCBC] text-[#3D5A40] hover:bg-[#F4EFE6] transition-colors"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6">
                {proofDone ? (
                  <p
                    data-testid="proof-uploaded"
                    className="text-sm font-semibold text-[#3D5A40] bg-[#EAF2E9] rounded-xl py-3 px-4"
                  >
                    Bukti transfer sudah kami terima. Pesanan Anda akan segera diproses.
                  </p>
                ) : (
                  <>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={uploadProof}
                      data-testid="proof-file-input"
                    />
                    <button
                      data-testid="upload-proof-btn"
                      onClick={() => fileRef.current?.click()}
                      disabled={proofUploading}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#3D5A40] hover:bg-[#2E4530] disabled:opacity-50 text-[#FAF7EE] font-bold text-sm transition-colors shadow-md"
                    >
                      <Upload className="w-4 h-4" />
                      {proofUploading ? "Mengunggah..." : "Unggah Bukti Transfer"}
                    </button>
                    <p className="text-[11px] text-[#7E8F7A] mt-3">
                      Simpan kode pesanan Anda untuk konfirmasi status pesanan dan untuk menulis{" "}
                      <a href="#ulasan" className="underline">ulasan</a> setelah pesanan selesai.
                    </p>
                  </>
                )}
              </div>

              {wa.number && (
                <div className="mt-6 p-5 rounded-2xl bg-[#EAF2E9] border border-[#CFE0CC] text-left">
                  <span className="text-xs font-bold uppercase tracking-wider text-[#3D5A40] block mb-1">
                    Notifikasi & Cek Status via WhatsApp
                  </span>
                  <p className="text-xs text-[#5E6D5B] mb-3">
                    Kirim konfirmasi pesanan Anda ke WhatsApp kami di{" "}
                    <span data-testid="whatsapp-number" className="font-bold text-[#1E2B1D]">
                      {wa.display}
                    </span>{" "}
                    agar kami bisa segera memproses dan mengabari status pesanan.
                  </p>
                  <a
                    href={waLink(wa.number, orderWaMessage(order, formatRupiah))}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="whatsapp-confirm-btn"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#25D366] hover:bg-[#1EBE5A] text-white font-bold text-sm transition-colors shadow-md"
                  >
                    <MessageCircle className="w-4 h-4" />
                    Konfirmasi & Cek Status di WhatsApp
                  </a>
                </div>
              )}
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-6" data-testid="checkout-form">
              <div className="p-4 rounded-2xl bg-[#F4EFE6] border border-[#E0D7C6]">
                <span className="text-xs font-bold uppercase tracking-wider text-[#354634] block mb-2">
                  Isi Keranjang
                </span>
                {items.length === 0 ? (
                  <p data-testid="checkout-cart-empty" className="text-xs text-[#7E8F7A]">
                    Keranjang kosong — pilih produk dari katalog di atas terlebih dahulu.
                  </p>
                ) : (
                  <ul className="space-y-1.5 text-xs text-[#354634]">
                    {items.map((i) => (
                      <li key={i.product.id} className="flex justify-between gap-4">
                        <span>
                          {i.product.name} <span className="text-[#7E8F7A]">× {i.qty}</span>
                        </span>
                        <span className="font-semibold">{formatRupiah(i.qty * i.product.price)}</span>
                      </li>
                    ))}
                    <li className="flex justify-between gap-4 pt-2 border-t border-[#E0D7C6] font-bold text-[#203120]">
                      <span>Total</span>
                      <span data-testid="checkout-total">{formatRupiah(total)}</span>
                    </li>
                  </ul>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-[#354634] mb-2">
                    Nama Pemesan
                  </label>
                  <input
                    required
                    value={form.buyer_name}
                    onChange={set("buyer_name")}
                    data-testid="checkout-name-input"
                    type="text"
                    placeholder="Nama lengkap Anda"
                    className="w-full px-4 py-3 rounded-xl border border-[#D5CCBC] bg-white text-xs text-[#1E2B1D] focus:outline-none focus:ring-2 focus:ring-[#3D5A40]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-[#354634] mb-2">
                    Nomor Kontak / WhatsApp
                  </label>
                  <input
                    required
                    value={form.phone}
                    onChange={set("phone")}
                    data-testid="checkout-phone-input"
                    type="text"
                    placeholder="08xxxxxxxxxx"
                    className="w-full px-4 py-3 rounded-xl border border-[#D5CCBC] bg-white text-xs text-[#1E2B1D] focus:outline-none focus:ring-2 focus:ring-[#3D5A40]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#354634] mb-2">
                  Alamat Pengiriman (Seluruh Nusantara)
                </label>
                <textarea
                  required
                  value={form.address}
                  onChange={set("address")}
                  data-testid="checkout-address-input"
                  rows="2"
                  placeholder="Nama jalan, nomor rumah, kecamatan, kota/kabupaten, kode pos"
                  className="w-full px-4 py-3 rounded-xl border border-[#D5CCBC] bg-white text-xs text-[#1E2B1D] focus:outline-none focus:ring-2 focus:ring-[#3D5A40]"
                ></textarea>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#354634] mb-2">
                  Teks Kartu Ucapan
                </label>
                <input
                  value={form.note}
                  onChange={set("note")}
                  data-testid="checkout-note-input"
                  type="text"
                  placeholder="Contoh: Selamat atas wisuda / Turut berduka cita dari..."
                  className="w-full px-4 py-3 rounded-xl border border-[#D5CCBC] bg-white text-xs text-[#1E2B1D] focus:outline-none focus:ring-2 focus:ring-[#3D5A40]"
                />
                <GreetingCardPreview note={form.note} sender={form.buyer_name} />
              </div>

              <div className="p-5 rounded-2xl bg-[#F4EFE6] border border-[#E0D7C6]">
                <span className="text-xs font-bold uppercase tracking-wider text-[#3D5A40] block mb-2">
                  Instruksi Pembayaran Transfer Bank Manual
                </span>
                <p className="text-xs text-[#5E6D5B] mb-4">
                  Setelah menekan tombol konfirmasi, silakan transfer ke salah satu rekening resmi
                  Puspita Florist:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  {banks.map((b, i) => (
                    <div key={i} className="p-3 bg-white rounded-xl border border-[#E8E2D5]">
                      <span className="font-bold text-[#1E2B1D] block">{b.bank}</span>
                      <span className="font-mono text-xs text-[#3D5A40] font-bold block mt-1">
                        {b.number}
                      </span>
                      <span className="text-[10px] text-[#7E8F7A] block">a.n. {b.holder}</span>
                    </div>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting || items.length === 0}
                data-testid="checkout-submit-btn"
                className="w-full py-4 rounded-xl bg-[#3D5A40] hover:bg-[#2E4530] disabled:opacity-40 disabled:cursor-not-allowed text-[#FAF7EE] font-bold text-sm transition-colors shadow-md"
              >
                {submitting ? "Memproses..." : "Konfirmasi Pesanan & Checkout"}
              </button>
              <p className="text-[11px] text-center text-[#7E8F7A]">
                Privasi data Anda terjaga. Bukti transfer dapat dikirimkan langsung setelah checkout
                selesai.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
